import markUrl from "./brand/qualify-mark-animated.svg";
import { strings } from "./brand/strings";
import { ServiceStatus } from "./ServiceStatus";

interface SignInPageProps {
  readonly onSignIn: () => void;
  readonly failed: boolean;
}

/**
 * The public gate. It offers exactly one action.
 *
 * There is no password field on it and there never will be: the credential is typed into
 * the identity provider's own page, and this product does not handle one
 * (SECURITY_STANDARDS rule S9).
 *
 * The composition is the port of design/welcome/index-final.html, accepted by Mahmoud on
 * 2026-09-20. Every size and gap comes from the modular scale in brand/tokens.css.
 *
 * THE MARK IS AN <img>, NOT INLINE SVG, AND THAT IS DELIBERATE.
 * The animation has to stop for a reader who has asked for reduced motion. The SVG
 * carries its own `prefers-reduced-motion` query, and an SVG referenced by <img> is
 * rendered by the browser with the user's own preference in force: measured on this
 * machine, the standalone file reports three running animations by default and zero
 * under the preference. Inlining it would mean either a hand port of the SVG into JSX,
 * which drifts from the asset silently, or `dangerouslySetInnerHTML`, which buys nothing
 * here. The mark is decorative: the name is in the heading beside it.
 */
export function SignInPage({ onSignIn, failed }: SignInPageProps): React.JSX.Element {
  return (
    <div className="q-gate">
      <div className="q-sky" aria-hidden="true">
        <div className="q-stars q-stars--1" />
        <div className="q-stars q-stars--2" />
        <div className="q-stars q-stars--3" />
      </div>

      <main className="q-gate__main">
        <img className="q-gate__mark" src={markUrl} alt={strings.gate.markAlt} />

        <h1 className="q-wordmark q-gate__wordmark">{strings.productName}</h1>

        <div className="q-gate__taglines">
          <p className="q-gate__descriptor">{strings.gate.descriptor}</p>
          <p className="q-gate__tagline">{strings.gate.tagline}</p>
        </div>

        <button type="button" className="q-button q-gate__action" onClick={onSignIn}>
          {strings.gate.signIn}
        </button>

        {failed && (
          <p className="q-gate__alert" role="alert">
            {strings.gate.signInFailed}
          </p>
        )}
      </main>

      <footer className="q-gate__foot">
        <ServiceStatus />
        <p className="q-gate__standards">{strings.gate.standards}</p>
        <p className="q-gate__colophon">{strings.gate.colophon}</p>
      </footer>
    </div>
  );
}
